import { G } from '@mobily/ts-belt';
import { useCallback, useMemo, useState } from 'react';
import { HttpError, RaRecord, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { match } from 'ts-pattern';

import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';
import {
  B2bWithdrawalDocument,
  PostB2bWithdrawalRequestEndpoint,
} from '@Common/types/bo/b2b/withdrawals';
import { BOResourceAll } from '@Common/types/bo_external/resources';
import {
  EARLY_WITHDRAWAL_PENDING_ORDERS,
  FUNDING_WITHDRAWAL_INSUFFICIENT_BALANCE,
  WITHDRAWAL_DECEASED_REQUIRES_THIRD_PARTY,
} from '@Common/types/error_messages';
import { ZodUkAccountNumber, ZodUkSortCode } from '@Common/types/zod/bank_account';

import { CreateInDialogButton, StepperForm, StepperFormStep } from '@sh4res/bo-common/components';
import {
  UploadPresignedUrl,
  useResourceBasePath,
  useUploadPresignedUrl,
} from '@sh4res/bo-common/hooks';
import {
  createWithdrawalAllocations,
  isThirdPartyOnlyWithdrawal,
} from '@sh4res/bo-common/resources/b2b/withdrawals/common';
import { skipsWithdrawalIdentityDocuments } from '@sh4res/bo-common/resources/b2b/withdrawals/skipsWithdrawalIdentityDocuments';
import { AmountsStep } from '@sh4res/bo-common/resources/b2b/withdrawals/steps/AmountsStep';
import { DocumentsStep } from '@sh4res/bo-common/resources/b2b/withdrawals/steps/DocumentsStep';
import { GeneralInfoStep } from '@sh4res/bo-common/resources/b2b/withdrawals/steps/GeneralInfoStep';
import { PaymentStep } from '@sh4res/bo-common/resources/b2b/withdrawals/steps/PaymentStep';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type ThirdPartyAccountType = 'iban' | 'intl' | 'scan';
type WithdrawalDocumentInput = { rawFile?: File; title: string };

const buildThirdPartyBankAccount = (
  data: RaRecord,
  presignedPath: string | null,
  documentName: string | null,
): PostB2bWithdrawalRequestEndpoint['Client']['RequestBody']['third_party_bank_account'] => {
  const tp = data.third_party_bank_account;
  const accountType: ThirdPartyAccountType = tp.account_type || 'iban';
  const isInstitution = tp.entity_type === 'institution';
  const common = {
    presigned_path: presignedPath,
    document_name: documentName,
    first_name: isInstitution ? tp.company_name : tp.first_name,
    last_name: isInstitution ? null : (tp.last_name ?? null),
    address: tp.address ?? null,
  };
  return match(accountType)
    .with('scan', () => ({
      ...common,
      account_type: 'scan' as const,
      sort_code: ZodUkSortCode.parse(tp.sort_code),
      account_number: ZodUkAccountNumber.parse(tp.account_number),
      bic: tp.bic,
    }))
    .with('intl', () => ({
      ...common,
      account_type: 'intl' as const,
      account_number: tp.account_number,
      bic: tp.bic,
    }))
    .with('iban', () => ({
      ...common,
      account_type: 'iban' as const,
      iban: tp.iban,
      bic: tp.bic,
    }))
    .exhaustive();
};

const uploadWithdrawalDocument = async (
  upload: UploadPresignedUrl,
  document: WithdrawalDocumentInput,
  type: B2bWithdrawalDocument['type'],
  dateOfExpiry?: B2bWithdrawalDocument['date_of_expiry'],
): Promise<B2bWithdrawalDocument> => {
  if (!(document.rawFile instanceof File)) {
    throw new Error('Missing withdrawal document file');
  }

  const uploadResult = await upload(document.rawFile);
  if (!uploadResult) {
    throw new Error(`Unable to upload withdrawal document ${document.title}`);
  }

  return {
    type,
    presigned_path: uploadResult.path,
    name: document.title || document.rawFile.name,
    ...(G.isNotNullable(dateOfExpiry) ? { date_of_expiry: dateOfExpiry } : {}),
  };
};

const createHandleTransform =
  (upload: UploadPresignedUrl) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (data: RaRecord): Promise<any> => {
    const { from } = data;

    const allocationsData: Record<string, number> = data.allocations?.[from] || {};

    const allocations = createWithdrawalAllocations(allocationsData, from);

    const identityDocuments: WithdrawalDocumentInput[] = data.documents_identity ?? [];
    const supportingDocuments: WithdrawalDocumentInput[] = data.documents_supporting ?? [];

    const documents: B2bWithdrawalDocument[] = await Promise.all(
      identityDocuments.map(async (document: WithdrawalDocumentInput) =>
        uploadWithdrawalDocument(upload, document, 'proof_of_identity', data.date_of_expiry),
      ),
    );

    if (data.from === 'early') {
      documents.push(
        ...(await Promise.all(
          supportingDocuments.map(async (document: WithdrawalDocumentInput) =>
            uploadWithdrawalDocument(upload, document, 'early_withdrawal_request'),
          ),
        )),
      );
    }

    const isThirdParty = data.beneficiary_id === 'third_party';

    let thirdPartyBankAccount:
      | PostB2bWithdrawalRequestEndpoint['Client']['RequestBody']['third_party_bank_account']
      | undefined;
    if (isThirdParty && G.isNotNullable(data.third_party_bank_account)) {
      const ribFile = data.third_party_bank_account.rib;
      let presignedPath = null;
      let documentName = null;

      if (ribFile?.rawFile instanceof File) {
        const uploadResult = await upload(ribFile.rawFile);
        if (uploadResult) {
          presignedPath = uploadResult.path;
          documentName = ribFile.title || ribFile.rawFile.name;
        }
      }

      thirdPartyBankAccount = buildThirdPartyBankAccount(data, presignedPath, documentName);
    }

    return {
      employee_id: data.b2b_employee_id,
      company_id: data.b2b_company_id,
      beneficiary_id: isThirdParty ? undefined : data.beneficiary_id,
      third_party_bank_account: thirdPartyBankAccount,
      early_event_date: data.date_of_event ?? null,
      early_reason: from === 'early' ? (data.early_reason ?? null) : null,
      allocations,
      documents,
    };
  };

const getWithdrawalErrorMessage = (error: HttpError) =>
  match(error.body)
    .with({ message: EARLY_WITHDRAWAL_PENDING_ORDERS }, () => ({
      message: 'resources.bo_common.b2b.withdrawals.create.errors.pending_orders',
    }))
    .with({ message: WITHDRAWAL_DECEASED_REQUIRES_THIRD_PARTY }, () => ({
      message: 'resources.bo_common.b2b.withdrawals.create.errors.deceased_requires_third_party',
    }))
    .with({ message: FUNDING_WITHDRAWAL_INSUFFICIENT_BALANCE }, () => ({
      message: 'resources.bo_common.b2b.withdrawals.create.errors.insufficient_balance',
    }))
    .otherwise(() => ({
      message: 'resources.bo_common.b2b.withdrawals.create.errors.failure',
      messageArgs: { error: getErrorMessage(error) },
    })) as { message: string; messageArgs?: Record<string, string> };

type B2bWithdrawalsCreateProps = {
  b2bCompanyId?: number;
};

export function B2bWithdrawalsCreate({ b2bCompanyId }: B2bWithdrawalsCreateProps = {}) {
  const [b2bEmployee, setB2bEmployee] = useState<GetB2bEmployeesOneResponse | null>(null);
  const notify = useNotify();
  const { upload } = useUploadPresignedUrl();
  const resourceBasePath = useResourceBasePath();

  const handleTransform = useMemo(() => createHandleTransform(upload), [upload]);

  const steps: StepperFormStep[] = useMemo(
    () => [
      {
        label: 'resources.bo_common.b2b.withdrawals.create.steps.1',
        content: <GeneralInfoStep b2bEmployee={b2bEmployee} setB2bEmployee={setB2bEmployee} />,
        validate: ({ getValues }) => {
          const { from, eligible_allocations, is_international, locked_funds } = getValues();
          if (!from) {
            return {
              isValid: false,
              message:
                locked_funds?.suspended > 0 && locked_funds.total === 0
                  ? 'resources.bo_common.b2b.withdrawals.create.suspended.not_withdrawable'
                  : 'resources.bo_common.b2b.withdrawals.create.labels.no_funds',
            };
          }
          if (
            from === 'early' &&
            !is_international &&
            (!eligible_allocations || eligible_allocations.locked === 0)
          ) {
            return {
              isValid: false,
              message:
                'resources.bo_common.b2b.withdrawals.create.validation.no_eligible_allocations',
            };
          }
          return { isValid: true };
        },
      },
      {
        label: 'resources.bo_common.b2b.withdrawals.create.steps.2',
        content: <AmountsStep />,
        validate: ({ getValues }) => {
          const { from, allocations } = getValues();
          const allocationsData: Record<string, number> = allocations?.[from] || {};

          const hasNonZeroAmount = Object.values(allocationsData).some((amount: number | null) => {
            if (G.isNullable(amount)) {
              return false;
            }
            return amount > 0;
          });

          if (!hasNonZeroAmount) {
            return {
              isValid: false,
              message:
                'resources.bo_common.b2b.withdrawals.create.validation.non_zero_allocation_required',
            };
          }

          return { isValid: true };
        },
      },
      {
        label: 'resources.bo_common.b2b.withdrawals.create.steps.3',
        content: <PaymentStep />,
        validate: ({ getValues }) => {
          const { beneficiary_id, third_party_bank_account, is_deceased, early_reason } =
            getValues();

          if (G.isNullable(beneficiary_id)) {
            return {
              isValid: false,
              message: 'resources.bo_common.b2b.withdrawals.create.validation.beneficiary_required',
            };
          }

          if (
            isThirdPartyOnlyWithdrawal({ isDeceased: is_deceased, earlyReason: early_reason }) &&
            beneficiary_id !== 'third_party'
          ) {
            return {
              isValid: false,
              message:
                'resources.bo_common.b2b.withdrawals.create.errors.deceased_requires_third_party',
            };
          }

          // If third-party option selected, validate the bank account fields
          if (beneficiary_id === 'third_party') {
            const isInstitution = third_party_bank_account?.entity_type === 'institution';
            const nameField = isInstitution
              ? third_party_bank_account?.company_name
              : third_party_bank_account?.first_name;

            const hasBaseFields =
              G.isNotNullable(third_party_bank_account) &&
              G.isNotNullable(nameField) &&
              nameField.trim() !== '' &&
              G.isNotNullable(third_party_bank_account.bic) &&
              third_party_bank_account.bic.trim() !== '' &&
              G.isNotNullable(third_party_bank_account.rib?.rawFile);

            const hasValidIban =
              third_party_bank_account?.account_type === 'iban' &&
              G.isNotNullable(third_party_bank_account.iban) &&
              third_party_bank_account.iban.trim() !== '';

            const hasValidIntl =
              third_party_bank_account?.account_type === 'intl' &&
              G.isNotNullable(third_party_bank_account.account_number) &&
              third_party_bank_account.account_number.trim() !== '';

            const hasValidScan =
              third_party_bank_account?.account_type === 'scan' &&
              G.isNotNullable(third_party_bank_account.sort_code) &&
              ZodUkSortCode.safeParse(third_party_bank_account.sort_code).success &&
              G.isNotNullable(third_party_bank_account.account_number) &&
              ZodUkAccountNumber.safeParse(third_party_bank_account.account_number).success;

            const hasValidThirdParty =
              hasBaseFields && (hasValidIban || hasValidIntl || hasValidScan);

            if (!hasValidThirdParty) {
              return {
                isValid: false,
                message:
                  'resources.bo_common.b2b.withdrawals.create.validation.third_party_required',
              };
            }
          }

          return { isValid: true };
        },
      },
      ...(isBoExternal()
        ? []
        : [
            {
              label: 'resources.bo_common.b2b.withdrawals.create.steps.4',
              content: <DocumentsStep />,
              hidden: (values: FieldValues) =>
                skipsWithdrawalIdentityDocuments({
                  from: values.from,
                  employeeStatus: b2bEmployee?.status,
                  allocations: values.allocations,
                  plans: values.locked_funds?.plans,
                }),
            },
          ]),
    ],
    [b2bEmployee, setB2bEmployee],
  );

  const onError = useCallback(
    (error: unknown) => {
      if (!(error instanceof HttpError)) {
        notify('resources.bo_common.b2b.withdrawals.create.errors.failure', {
          type: 'error',
          messageArgs: { error: getErrorMessage(error) },
        });

        return;
      }

      const { message, messageArgs } = getWithdrawalErrorMessage(error);

      notify(message, {
        type: 'error',
        messageArgs,
        multiLine: messageArgs ? true : false,
      });
    },
    [notify],
  );

  const withdrawalResource = `${resourceBasePath}withdrawals` as Exclude<BOResourceAll, '*'>;

  return (
    <CreateInDialogButton
      resource={withdrawalResource}
      testId="BoExternalB2bWithdrawalsCreate_CreateButton"
      fullWidth
      maxWidth="md"
      transform={handleTransform}
      mutationOptions={{ onError }}
      title="resources.bo_common.b2b.withdrawals.create.title">
      <StepperForm
        steps={steps}
        defaultValues={{ b2b_company_id: b2bCompanyId }}
        permissions={{ resource: withdrawalResource, action: 'create' }}
      />
    </CreateInDialogButton>
  );
}
